import _ from 'underscore';
import { Base } from './base';
import { IValidationRule, IValidatorMessage } from '../types';

/**
 * Define a type validator which check the value type is an array
 * @param {I18nModel} i18n - provide callback getString to get error message for Base
 */
class Type extends Base {
  isValidValue(value: any): boolean {
    return _.isArray(value);
  }
}

/**
 * Define a size validator which checks the size of array between min and max
 * @param {int} min - the min size of array, default be 0
 * @param {int} max - the max size of array
 * @param {I18nModel} i18n - provide callback getString to get error message when validate failed, will
 * be called with args 'Validation_Array_Size_Between' or 'Validation_Array_Size_Max'
 */
class Size extends Base {
  min: number;

  max: number;

  message: string;

  constructor(min: number, max: number, i18n: I18nModel) {
    super(i18n);

    this.min = min || 0;
    this.max = max;
    if (this.min) {
      this.message = i18n.getString('Validation_Array_Size_Between', { min: this.min, max });
    } else {
      this.message = i18n.getString('Validation_Array_Size_Max', { max });
    }
  }

  isValidValue(value: any): boolean {
    const valueSize = _.size(value);

    return _.isArray(value) && valueSize >= this.min && valueSize <= this.max;
  }
}

function passAll(validators: IValidationRule | IValidationRule[], value: any): boolean {
  return _.every(validators, (validator) => validator.isValid(value));
}

function sizeNotFits(value: any, validators: IValidationRule[], additionalValidators: boolean | IValidationRule[]): boolean {
  return !additionalValidators && _.size(value) > _.size(validators);
}

/**
 * Create an array validator which inspects each item of an array by validators passed in.
 * @param {IValidationRule[]} validators - validators for all items, each item should pass all validators.
 * @param {I18nModel} i18n - provide callback getString to get error message, will be called with args
 * 'Validation_Base_Field_Not_Valid' or 'Validation_Array_Items_Invalid'
 */
class Item extends Base {
  message: (value: any[]) => string;

  validators: IValidationRule[];

  constructor(validators: IValidationRule[], i18n: I18nModel) {
    super(i18n);

    this.validators = validators;
    this.message = (value: any[]) => {
      if (!_.isArray(value)) {
        return i18n.getString('Validation_Base_Field_Not_Valid');
      }

      return i18n.getString('Validation_Array_Items_Invalid');
    };
  }

  /**
   * override function, check whether value is valid
   * @param {array} value - value to validate
   * @return {boolean} - whether value is valid
   */
  isValidValue(value: any): boolean {
    return _.isArray(value) && _.isEmpty(this.invalidItems(value));
  }

  /**
   * Filter out invalid items.
   * @param {array} value - value to verify
   * @return {array} - return invalidate items
   * @see http://json-schema.org/latest/json-schema-validation.html#anchor37 for understanding conditions of successful validation.
   */
  invalidItems(value: any): any {
    return _.omit(value, _.partial(passAll, this.validators));
  }
}

/**
 * Create an array validator which inspects items of an array.
 * @param {IValidationRule[]} validators - validators for first _.size(validator) items.
 * @param {(IValidationRule[]|boolean)} additionalValidators - boolean as allowing or
 * not additional items, IValidator[] as validators for additional items.
 * @param {I18nModel} i18n - provide callback getString to get error message
 */
class Items extends Base {
  message: IValidatorMessage;

  validators: IValidationRule[];

  additionalValidators: boolean | IValidationRule[];

  constructor(validators: IValidationRule[], additionalValidators: boolean | IValidationRule[], i18n: I18nModel) {
    super(i18n);

    this.validators = validators;
    this.additionalValidators = additionalValidators;
    this.message = (value: any[]) => {
      if (!_.isArray(value)) {
        return i18n.getString('Validation_Base_Field_Not_Valid');
      }

      if (sizeNotFits(value, validators, additionalValidators)) {
        return i18n.getString('Validation_Array_Size_Max', { max: _.size(validators) });
      }

      return i18n.getString('Validation_Array_Items_Invalid');
    };
  }

  isValidValue(value: any): boolean {
    return _.isArray(value) && _.isEmpty(this.invalidItems(value));
  }

  /**
   * Filter out invalid items.
   *
   * when additionalValidators is false, means max length of array is _.size(validators);
   * when additionalValidators is true, no validation for additional items;
   * when additionalValidators is IValidator[], it will be applied to additional
   * items not covered by validators.
   * @param {array} value - value to verify
   * @return {array} - return invalid items
   * @see http://json-schema.org/latest/json-schema-validation.html#anchor37 for understanding conditions of successful validation.
   */
  invalidItems(value: any[]): any {
    const { validators } = this;
    let { additionalValidators } = this;

    if (sizeNotFits(value, validators, additionalValidators)) {
      return value;
    }

    // additionalValidators could be validators or boolean
    if (additionalValidators === true) {
      additionalValidators = [];
    }

    return _.omit(value, (val, index) => {
      const vs = validators[index] || additionalValidators as IValidationRule[];
      return passAll(vs, val);
    });
  }
}

/**
 * Create an array validator unique, check whether element unique in array
 * @param {I18nModel} i18n - provide callback getString, will be called
 * with args 'Validation_Array_Items_Duplicate'
 */
class Unique extends Base {
  message: string;

  constructor(i18n: I18nModel) {
    super(i18n);

    this.message = i18n.getString('Validation_Array_Items_Duplicate');
  }

  isValidValue(value: any): boolean {
    return _.isArray(value) && _.size(_.uniq(value)) === _.size(value);
  }
}

/**
 * Create array type validate instance
 * @param {I18nModel} i18n - provide callback getString to get error message
 * @return {IValidationRule} - array type validator
 */
export function type(i18n: I18nModel): IValidationRule {
  return new Type(i18n);
}

/**
 * Create size validate instance
 * @param {int} min - the min size of array
 * @param {int} max - the max size of array
 * @param {I18nModel} i18n - provide callback getString to get error message
 * @returns {IValidationRule} - array size validator
 */
export function size(min: number, max: number, i18n: I18nModel): IValidationRule {
  return new Size(min, max, i18n);
}

/**
 * Create item validate instance
 * @param {IValidationRule[]} validators - validators for all items, each item should pass all validators.
 * @param {I18nModel} i18n - provide callback getString to get error message, will be called with args
 * 'Validation_Base_Field_Not_Valid' or 'Validation_Array_Items_Invalid'
 * @returns {IValidationRule} - array item validator
 */
export function item(validators: IValidationRule[], i18n: I18nModel): IValidationRule {
  return new Item(validators, i18n);
}

/**
 * Create an array items validator instance
 * @param {IValidationRule[]} validators - validators for first _.size(validator) items.
 * @param {(IValidationRule[]|boolean)} additionalValidators - boolean as allowing or
 * not additional items, IValidator[] as validators for additional items.
 * @param {I18nModel} i18n - provide callback getString to get error message
 * @returns {IValidationRule} - array items validator
 */
export function items(validators: IValidationRule[], additionalValidators: boolean | IValidationRule[], i18n: I18nModel): IValidationRule {
  return new Items(validators, additionalValidators, i18n);
}

/**
 * Create an array unique validate instance
 * @param {I18nModel} i18n - provide callback getString, will be called
 * with args 'Validation_Array_Items_Duplicate'
 * @returns {IValidationRule} - array unique validator
 */
export function unique(i18n: I18nModel): IValidationRule {
  return new Unique(i18n);
}
